import styles from './Reviews.module.scss'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Text from 'shared/ui/Text/Text'
import StarRating from 'shared/ui/StarRating/StarRating'
import Like from 'shared/assets/icons/like.svg'
import Button from 'shared/ui/Button/Button'

import data from 'shared/assets/data/reviews.json'

import { ReviewCard } from './ReviewCard/ReviewCard'
import { Spinner } from 'shared/ui/Spinner/Spinner'
// import { importReviews } from 'shared/assets/lib/importReviews'

export const siteNames = {
  fintok_es: 'fintok_es',
  creditsfast: 'creditsfast',
  milprestamos: 'milprestamos',
  prestamopro: 'prestamopro',
} as const

export type SiteNames = ObjectValues<typeof siteNames>

interface Review {
  avatarColor: string
  name: string
  amount: string
  rating: number
  time: string
  review: string
  likes: number
}

// const data = require('shared/assets/data/reviews.json5')
console.log(data)

const AVERAGE_RATING = 4.9
const REVIEWS_TO_SHOW = 4
const reviewsData = data as Record<SiteNames, Review[]>

interface ReviewsProps {
  siteName: SiteNames
}

export const Reviews: FC<ReviewsProps> = (props) => {
  const { siteName } = props
  const reviewsRef = useRef<HTMLUListElement>(null)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [className, setClassName] = useState(styles.Reviews)
  const [reviews, setReviews] = useState<Review[]>(
    reviewsData?.[siteName] || []
  )
  const [lastReviewIndex, setLastReviewIndex] = useState(
    reviews
      ? reviews.length > REVIEWS_TO_SHOW - 1
        ? REVIEWS_TO_SHOW - 1
        : reviews.length
      : 0
  )
  const [visibleReviews, setVisibleReviews] = useState<Review[]>(
    reviews ? reviews.slice(0, REVIEWS_TO_SHOW) : []
  )

  const onClickShowMore = useCallback(() => {
    setClassName(`${styles.Reviews} ${styles.ReviewsLoading}`)
    setIsLoading(true)
    let reviewsToShow: Review[] = []

    if (lastReviewIndex + REVIEWS_TO_SHOW <= reviews.length - 1) {
      reviewsToShow = reviews.slice(
        lastReviewIndex + 1,
        lastReviewIndex + 1 + REVIEWS_TO_SHOW
      )
      setLastReviewIndex(lastReviewIndex + REVIEWS_TO_SHOW)
    } else {
      const reviewsFromStart =
        REVIEWS_TO_SHOW - (reviews.length - lastReviewIndex - 1)
      reviewsToShow = [
        ...reviews.slice(lastReviewIndex + 1),
        ...reviews.slice(0, reviewsFromStart),
      ]

      setLastReviewIndex(reviewsFromStart - 1)
    }
    setTimeout(() => {
      setVisibleReviews(reviewsToShow)
      setClassName(styles.Reviews)
      setIsLoading(false)
    }, 1000)
  }, [lastReviewIndex, reviews])

  useEffect(() => {
    setReviews(reviewsData[siteName])
  }, [siteName])

  useEffect(() => {
    setVisibleReviews(reviews ? reviews.slice(0, REVIEWS_TO_SHOW) : [])
  }, [reviews])

  useEffect(() => {
    if (isLoading) {
      reviewsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [isLoading])

  const userReviews = useMemo(() => {
    const firstColumn = visibleReviews.slice(0, 2)
    const secondColumn = visibleReviews.slice(2, 4)
    return (
      <>
        <div className={styles.column}>
          {firstColumn.map(
            ({ avatarColor, name, amount, rating, time, review, likes }) => {
              const firstLetter = name[0]
              return (
                <li className={styles.ReviewsListItem} key={review}>
                  <ReviewCard
                    name={name}
                    firstLetter={firstLetter}
                    amount={amount}
                    review={review}
                    likes={likes}
                    time={time}
                    rating={rating}
                    avatarColor={avatarColor}
                  />
                </li>
              )
            }
          )}
        </div>
        <div className={styles.column}>
          {secondColumn.map(
            ({ avatarColor, name, amount, rating, time, review, likes }) => {
              const firstLetter = name[0]
              return (
                <li className={styles.ReviewsListItem} key={review}>
                  <ReviewCard
                    name={name}
                    firstLetter={firstLetter}
                    amount={amount}
                    review={review}
                    likes={likes}
                    time={time}
                    rating={rating}
                    avatarColor={avatarColor}
                  />
                </li>
              )
            }
          )}
        </div>
      </>
    )
  }, [visibleReviews])

  return (
    <div className={className}>
      <div className={styles.ReviewsHeader}>
        <div className={styles.ReviewsHeaderRating}>
          <Text
            className={styles.AverageRating}
            title={String(AVERAGE_RATING)}
            titleSize="h3"
          />
          <StarRating rating={AVERAGE_RATING} starSize="m" />
          <Text
            className={styles.TotalRatings}
            paragraph="96,027 reseñas"
            paragraphSize="m"
          />
        </div>
        <Text
          className={styles.RatingVerify}
          paragraph="Las reseñas se verifican."
          paragraphSize="s"
        />
        <div className={styles.RatingSortWrapper}>
          <Text
            className={styles.SortBy}
            paragraph="Ordenar por"
            paragraphSize="m"
          />
          <Text
            className={styles.SortByTime}
            paragraph="Más relevantes"
            paragraphSize="xs"
          />
        </div>
      </div>
      <ul className={styles.ReviewsList} ref={reviewsRef}>
        {isLoading ? <Spinner /> : userReviews}
      </ul>
      <Button
        className={styles.ShowMoreButton}
        onClick={onClickShowMore}
        size="m"
        theme="neutral"
        disabled={isLoading}
      >
        Mostrar más
      </Button>
    </div>
  )
}
